import { A4, LETTER, tagRest, type FlowTemplate, type IconStyle, type RenderContext } from "./base";
import { familyScale, isFamilyId, type FamilyId } from "./fonts";
import type { IconGlyph } from "./icons";
import { trimTrailingSpace, type Node, type PageSpec } from "./ir";
import { buildSampleModel, type RenderResume, type ResumeSample } from "./model";
import { AtlasTemplate } from "./templates/atlas";
import { AuroraTemplate } from "./templates/aurora";
import { CodeTemplate } from "./templates/code";
import { ExecutiveTemplate } from "./templates/executive";
import { HorizonTemplate } from "./templates/horizon";
import { MeridianTemplate } from "./templates/meridian";
import { PrestigeTemplate } from "./templates/prestige";
import { StudioTemplate } from "./templates/studio";
import { SwissTemplate } from "./templates/swiss";
import { TimelineTemplate } from "./templates/timeline";
import { RenderTheme, rgb } from "./theme";

/** The ten designs, in the app's gallery order (template_catalog.dart). */
export const TEMPLATES: FlowTemplate[] = [
  new AuroraTemplate(),
  new ExecutiveTemplate(),
  new MeridianTemplate(),
  new HorizonTemplate(),
  new AtlasTemplate(),
  new SwissTemplate(),
  new TimelineTemplate(),
  new StudioTemplate(),
  new PrestigeTemplate(),
  new CodeTemplate(),
];

export const DEFAULT_TEMPLATE_ID = "aurora";

/** Curated accent swatches offered in the design panel (ARGB), as in the app. */
export const ACCENTS: number[] = [
  0xff0f766e, // teal
  0xff0f4c5c, // petrol
  0xff1f3a5f, // navy
  0xff2563eb, // blue
  0xff4f46e5, // indigo
  0xff6d28d9, // violet
  0xffbe185d, // raspberry
  0xffdc2626, // red
  0xffea580c, // orange
  0xff8a6a3b, // bronze
  0xff059669, // emerald
  0xff111827, // ink
];

/** Unknown and legacy ids fall back to Aurora. */
export function templateById(id: string | null | undefined): FlowTemplate {
  return TEMPLATES.find((t) => t.info.id === id) ?? TEMPLATES[0];
}

/** Sample variant n (1–7) starts in the n-th gallery template. */
export function defaultTemplateFor(variantNo: number): FlowTemplate {
  const i = Math.max(0, variantNo - 1) % TEMPLATES.length;
  return TEMPLATES[i];
}

/** Everything one render depends on besides the template's own design. */
export type RenderOptions = {
  model: RenderResume;
  templateId?: string | null;
  pageSize?: "a4" | "letter";
  /** Typeface id for body text; anything else keeps the template's pairing. */
  bodyFont?: string | null;
  /** Typeface id for headings and the name; null follows the body/template. */
  headingFont?: string | null;
  /** Text-size multiplier (0.8–1.25). */
  fontScale?: number;
  /** Vertical-rhythm multiplier (0.78 compact … 1.22 relaxed). */
  density?: number;
  /** ARGB accent; null keeps the template's own. */
  accent?: number | null;
  /** ARGB text colour; null keeps the default slate ink. */
  textColor?: number | null;
  iconStyle?: IconStyle;
  /** The section-icon table (sectionIcons.ts) when icons beside headings are on. */
  sectionIcons?: Record<string, IconGlyph> | null;
  showSkillLevels?: boolean;
};

type Families = { body: FamilyId; heading: FamilyId; display: FamilyId; mono: FamilyId; userHeading: boolean };

/**
 * The app's `_resolveFamilies`: a chosen heading font sets headings and the
 * name; otherwise the template decides whether they follow the body font.
 */
function resolveFamilies(template: FlowTemplate, bodyFont?: string | null, headingFont?: string | null): Families {
  const f = template.fonts;
  const user = isFamilyId(bodyFont) ? bodyFont : null;
  const userHeading = isFamilyId(headingFont) ? headingFont : null;
  const body = user ?? f.body;
  const heading = userHeading ?? (user && (f.headingFollowsBody ?? true) ? user : (f.heading ?? body));
  const display = userHeading ?? (user && (f.displayFollowsBody ?? true) ? user : (f.display ?? heading));
  return { body, heading, display, mono: f.usesMono ? "jetbrains" : body, userHeading: !!userHeading };
}

export function renderContextFor(o: RenderOptions): { template: FlowTemplate; context: RenderContext } {
  const template = templateById(o.templateId);
  const fam = resolveFamilies(template, o.bodyFont, o.headingFont);
  const t = new RenderTheme({
    body: fam.body,
    heading: fam.heading,
    display: fam.display,
    mono: fam.mono,
    accent: rgb(o.accent ?? template.info.defaultAccent),
    fontScale: o.fontScale ?? 1,
    density: o.density ?? 1,
    // Only a user-chosen heading font is re-sized; template pairings are tuned.
    headingScale: fam.userHeading ? familyScale(fam.heading) / familyScale(fam.body) : 1,
    textColor: o.textColor != null ? rgb(o.textColor) : null,
  });
  const iconStyle = o.iconStyle ?? "filled";
  return {
    template,
    context: {
      model: template.info.supportsPhoto ? o.model : { ...o.model, photo: null },
      t,
      format: o.pageSize === "letter" ? LETTER : A4,
      iconStyle,
      showIcons: iconStyle !== "none",
      sectionIcons: o.sectionIcons ?? null,
      showSkillLevels: o.showSkillLevels ?? false,
    },
  };
}

/**
 * The page design for one render: trailing section gaps trimmed, and every
 * block tagged with the resume part that drew it ("personal" for the header,
 * contact panel and photo) so a preview can map clicks back to the editor.
 */
export function buildSpec(o: RenderOptions): PageSpec {
  const { template, context } = renderContextFor(o);
  const spec = template.build(context);
  const finish = (nodes: Node[]) => tagRest(trimTrailingSpace(nodes), "personal");
  return {
    ...spec,
    body: finish(spec.body),
    columns: spec.columns ? { ...spec.columns, left: finish(spec.columns.left), right: finish(spec.columns.right) } : undefined,
  };
}

/** The page design for one sample in one template. */
export function renderSample(sample: ResumeSample, templateId: string, year: number): PageSpec {
  return buildSpec({ model: buildSampleModel(sample, year), templateId });
}
